import React from 'react';
import MultiSelect from '../../ui/MultiSelect';
import { PERIOD_WINDOWS, windowSpan, type PeriodWindow } from '../../lib/funnel';
import { sourceLabel } from '../../lib/sourceNames';
import {
  FEED_STATE_CHOICES,
  FEED_STATE_LABEL,
  FEED_TYPE_CHOICES,
  FEED_TYPE_LABEL,
  type FeedStateGroup,
  type FeedType,
} from '../../lib/stageDetail';
import { FEED_ACTION_LABEL, FEED_ACTION_TYPES, type FeedActionType, type Severity } from '../../data/selectors/feed';

export const SEVERITY_CHOICES: readonly Severity[] = ['critical', 'high', 'medium', 'low'];
const SEVERITY_LABEL: Record<Severity, string> = { critical: 'Critical', high: 'High', medium: 'Medium', low: 'Low' };
/** The time filter is the Overview's period picker (client notes 10/26, item 29), plus "all", which filters nothing. */
export const TIME_CHOICES: readonly (PeriodWindow | 'all')[] = ['all', ...PERIOD_WINDOWS];

/** What the six filters have ticked. An empty list is no filter; `time` is 'all' for any time. */
export interface FeedFilterValue {
  sources: string[];
  severities: Severity[];
  states: FeedStateGroup[];
  types: FeedType[];
  actions: FeedActionType[];
  time: PeriodWindow | 'all';
}

export const NO_FILTER_VALUE: FeedFilterValue = {
  sources: [],
  severities: [],
  states: [],
  types: [],
  actions: [],
  time: 'all',
};

export function isFiltering(value: FeedFilterValue): boolean {
  return (
    value.sources.length > 0 ||
    value.severities.length > 0 ||
    value.states.length > 0 ||
    value.types.length > 0 ||
    value.actions.length > 0 ||
    value.time !== 'all'
  );
}

interface FeedFilterControlsProps {
  /** The sources to choose from. */
  sources: string[];
  value: FeedFilterValue;
  /** The filters that changed; the rest are as they were. */
  onChange: (patch: Partial<FeedFilterValue>) => void;
}

/**
 * The six filters of the Threat Feed (client notes 10/26, item 14): Source, Severity, State, Type, Time received and Action
 * type. The Threat Index uses the same controls (items 29 and 30: its time filter and its filters match the feed's), so the
 * two read alike. Where the ticks are kept is the screen's business: the feed keeps them in the address, the index in itself.
 */
const FeedFilterControls: React.FC<FeedFilterControlsProps> = ({ sources, value, onChange }) => (
  <>
    <MultiSelect
      className="tf-filter"
      label="Source"
      allLabel="All sources"
      many={(count) => `${count} sources`}
      options={sources.map((source) => ({ value: source, label: sourceLabel(source) }))}
      value={value.sources}
      onChange={(next) => onChange({ sources: next })}
    />
    <MultiSelect
      className="tf-filter"
      label="Severity"
      allLabel="All severities"
      many={(count) => `${count} severities`}
      options={SEVERITY_CHOICES.map((item) => ({ value: item, label: SEVERITY_LABEL[item] }))}
      value={value.severities}
      onChange={(next) => onChange({ severities: next })}
    />
    <MultiSelect
      className="tf-filter"
      label="State"
      allLabel="All states"
      many={(count) => `${count} states`}
      options={FEED_STATE_CHOICES.map((item) => ({ value: item, label: FEED_STATE_LABEL[item] }))}
      value={value.states}
      onChange={(next) => onChange({ states: next })}
    />
    <MultiSelect
      className="tf-filter"
      label="Type"
      allLabel="All types"
      many={(count) => `${count} types`}
      options={FEED_TYPE_CHOICES.map((item) => ({ value: item, label: FEED_TYPE_LABEL[item] }))}
      value={value.types}
      onChange={(next) => onChange({ types: next })}
    />
    <MultiSelect
      className="tf-filter"
      label="Time received"
      allLabel="Any time"
      single
      options={PERIOD_WINDOWS.map((item) => ({ value: item, label: `Last ${windowSpan(item)}` }))}
      value={value.time === 'all' ? [] : [value.time]}
      onChange={(next) => onChange({ time: next[0] ?? 'all' })}
    />
    <MultiSelect
      className="tf-filter"
      label="Action type"
      allLabel="All action types"
      options={FEED_ACTION_TYPES.map((item) => ({ value: item, label: FEED_ACTION_LABEL[item] }))}
      value={value.actions}
      onChange={(next) => onChange({ actions: next })}
    />
  </>
);

export default FeedFilterControls;
